import { useCallback, useEffect, useRef } from 'react';

import { ArrowButton } from '#/components/ArrowButton/ArrowButton';
import { useViewportSizeEffect } from '#/hooks/viewport/useViewportSizeEffect';
import { transition } from '#/theme/theme';

import { TabsItem } from './Item/TabsItem';
import type { Tab } from './Tab';
import {
  TabsContainer,
  TabsMarker,
  TabsUl,
  ArrowButtonContainer,
} from './Tabs.styled';

type Props<TValue extends string> = {
  tabs: Tab<TValue>[] | readonly Tab<TValue>[];
  value: TValue | undefined;
  onSelectTab?: (value: TValue) => void;
  layout: 'vertical-on-desktop' | 'horizontal' | 'full-width-mobile';
  variant: 'background' | 'marker' | 'compact-marker';
};

export function Tabs<T extends string>({
  tabs,
  value,
  onSelectTab,
  layout,
  variant,
}: Props<T>) {
  const tabsContainerRef = useRef<HTMLUListElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);
  const selectedTabIndex = value
    ? tabs.findIndex((tab) => tab.value === value)
    : 0;

  const hasMarker = variant === 'marker' || variant === 'compact-marker';

  const moveMarker = useCallback(
    (animate?: boolean) => {
      if (!hasMarker) return;

      const tabsContainer = tabsContainerRef.current;
      const marker = markerRef.current;

      if (!tabsContainer || !marker || selectedTabIndex === -1) return;

      const tabNode = tabsContainer.childNodes[selectedTabIndex];
      if (!tabNode) return;

      const { offsetLeft, offsetWidth, offsetTop, offsetHeight } =
        tabNode as HTMLDivElement;

      marker.style.width = `${offsetWidth}px`;
      marker.style.left = `${offsetLeft - tabsContainer.scrollLeft}px`;
      marker.style.top = `${
        offsetTop + offsetHeight - tabsContainer.scrollTop
      }px`;

      if (!animate) {
        marker.style.transition = 'none';
      }

      setTimeout(() => {
        marker.style.transition = transition;
      }, 0);
    },
    [hasMarker, selectedTabIndex],
  );

  useEffect(() => {
    moveMarker(true);
  }, [moveMarker]);

  useViewportSizeEffect(() => {
    moveMarker(true);
  });

  const updateTab = (num: number) => {
    const tabsContainerEl = tabsContainerRef?.current;

    if (tabsContainerEl && tabs[num]?.value) {
      const itemEl = tabsContainerEl.querySelector(`:nth-child(${num + 1})`);
      itemEl?.scrollIntoView({ container: 'nearest' } as ScrollIntoViewOptions);
      onSelectTab?.(tabs[num]?.value);
    }
  };

  const onClickNext = () => {
    updateTab(Math.abs((selectedTabIndex + 1) % tabs.length));
  };

  const onClickPrev = () => {
    updateTab(Math.abs((selectedTabIndex - 1) % tabs.length));
  };

  return (
    <TabsContainer $layout={layout} data-qa-id="tabs">
      <ArrowButtonContainer data-qa-id="prev-tab">
        <ArrowButton
          direction="left"
          onClick={onClickPrev}
          variant="secondary"
        />
      </ArrowButtonContainer>
      <TabsUl
        ref={tabsContainerRef}
        onScroll={() => moveMarker(false)}
        $layout={layout}
        $variant={variant}
      >
        {tabs.map((tab) => (
          <TabsItem
            key={tab.value}
            isSelected={tab.value === value}
            onClick={() => onSelectTab?.(tab.value)}
            href={tab.href ?? null}
            variant={variant}
            tabButtonId={tab.tabButtonId}
            tabPanelId={tab.tabPanelId}
            qaId={tab.qaId}
          >
            {tab.label}
          </TabsItem>
        ))}
      </TabsUl>
      <ArrowButtonContainer data-qa-id="next-tab">
        <ArrowButton
          direction="right"
          onClick={onClickNext}
          variant="secondary"
        />
      </ArrowButtonContainer>

      {hasMarker && <TabsMarker ref={markerRef} />}
    </TabsContainer>
  );
}
